
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

export interface PropertyCountData {
  venda_count: number;
  aluguel_count: number;
}

export interface TypeDistributionItem {
  tipo: string;
  count: number;
}

export interface PriceEvolutionItem {
  scrape_date_formatted: string;
  avg_price: number;
}

export interface HomeDashboardData {
  venda_count: number;
  aluguel_count: number;
  venda_tipos: TypeDistributionItem[];
  aluguel_tipos: TypeDistributionItem[];
  venda_evolucao: PriceEvolutionItem[];
  aluguel_evolucao: PriceEvolutionItem[];
  data_source?: string;
  message?: string;
}

// Cache reduzido para 1 hora para dados mais frescos
const CACHE_DURATION = 1 * 60 * 60 * 1000;
const HOME_DATA_CACHE_KEY = 'homeDashboardDataCache';

const getFormattedCache = (): { data: HomeDashboardData; timestamp: number } | null => {
  const cachedDataString = localStorage.getItem(HOME_DATA_CACHE_KEY);
  if (!cachedDataString) {
    console.log('[HomeDashboard] No cache found');
    return null;
  }
  
  try {
    const cachedData = JSON.parse(cachedDataString);
    console.log('[HomeDashboard] Cache found, age:', Math.round((Date.now() - cachedData.timestamp) / 60000), 'min');
    return cachedData;
  } catch (e) {
    console.error('[HomeDashboard] Error parsing cache');
    return null;
  }
};

const isCacheValid = (cache: { timestamp: number }): boolean => {
  const now = Date.now();
  const cacheAge = now - cache.timestamp;
  const isValid = cacheAge < CACHE_DURATION;
  console.log(`[HomeDashboard] Cache age: ${Math.round(cacheAge / 1000 / 60)} min, valid: ${isValid}`);
  return isValid;
};

const setFormattedCache = (data: HomeDashboardData): void => {
  try {
    const cacheObject = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(HOME_DATA_CACHE_KEY, JSON.stringify(cacheObject));
    console.log('[HomeDashboard] Cache updated');
  } catch (e) {
    console.error('[HomeDashboard] Error storing cache');
  }
};

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Função principal otimizada usando a nova tabela
const fetchDashboardDataFromTable = async (): Promise<{ data: any; error: any }> => {
  try {
    console.log('[HomeDashboard] Fetching data from home_dashboard_data table');
    
    const { data: responseData, error: responseError } = await supabase.rpc('get_home_dashboard_data_table');
    
    if (responseError) {
      console.error('[HomeDashboard] Error from new table function:', responseError);
      throw responseError;
    }
    
    if (!responseData) {
      throw new Error('Nenhum dado retornado da tabela');
    }
    
    console.log('[HomeDashboard] Data successfully fetched from table');
    return { data: responseData, error: null };
  } catch (error: any) {
    console.error('[HomeDashboard] Error fetching from table:', error);
    return { data: null, error };
  }
};

// Função de fallback usando a função antiga (apenas se necessário)
const fetchDashboardDataFallback = async (): Promise<{ data: any; error: any }> => {
  try {
    console.log('[HomeDashboard] Using fallback function');
    
    const { data: responseData, error: responseError } = await supabase.rpc('get_home_dashboard_data');
    
    if (responseError) {
      throw responseError;
    }
    
    if (!responseData) {
      throw new Error('Nenhum dado retornado do fallback');
    }
    
    console.log('[HomeDashboard] Fallback data successfully fetched');
    return { data: responseData, error: null };
  } catch (error: any) {
    console.error('[HomeDashboard] Fallback also failed:', error);
    return { data: null, error };
  }
};

export const useHomeDashboardData = () => {
  const [data, setData] = useState<HomeDashboardData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Check cache first
      const cachedData = getFormattedCache();
      if (cachedData && isCacheValid(cachedData)) {
        console.log('[HomeDashboard] Using cached data');
        setData(cachedData.data);
        setLoading(false);
        return;
      }
      
      // Pequeno delay para melhorar UX
      await delay(100);
      
      // Tentar buscar da nova tabela otimizada primeiro
      let result = await fetchDashboardDataFromTable();
      
      // Se falhar, tentar fallback apenas uma vez
      if (result.error || !result.data) {
        console.log('[HomeDashboard] Table fetch failed, trying fallback');
        result = await fetchDashboardDataFallback();
      }
      
      if (result.error || !result.data) {
        throw new Error(result.error?.message || 'Erro ao buscar dados do dashboard');
      }
      
      // Processar e validar dados
      const responseObj = result.data as Record<string, any>;
      
      const formattedData: HomeDashboardData = {
        venda_count: Number(responseObj.venda_count) || 0,
        aluguel_count: Number(responseObj.aluguel_count) || 0,
        venda_tipos: Array.isArray(responseObj.venda_tipos) ? responseObj.venda_tipos : [],
        aluguel_tipos: Array.isArray(responseObj.aluguel_tipos) ? responseObj.aluguel_tipos : [],
        venda_evolucao: Array.isArray(responseObj.venda_evolucao) ? responseObj.venda_evolucao : [],
        aluguel_evolucao: Array.isArray(responseObj.aluguel_evolucao) ? responseObj.aluguel_evolucao : [],
        data_source: responseObj.data_source,
        message: responseObj.message
      };
      
      setData(formattedData);
      setFormattedCache(formattedData);
      
      // Log da fonte dos dados para monitoramento
      if (formattedData.data_source) {
        console.log(`[HomeDashboard] Data source: ${formattedData.data_source}`);
      }
      
    } catch (err: any) {
      console.error('[HomeDashboard] Final error:', err.message);
      const errorMessage = 'Erro ao carregar dados do dashboard. Tente novamente.';
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);
  
  useEffect(() => {
    console.log('[HomeDashboard] Initializing optimized dashboard...');
    fetchData();
  }, [fetchData]);
  
  return {
    data,
    loading,
    error,
    refetch: fetchData
  };
};
